import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { booking } from '../api';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [disponibilites, setDisponibilites] = useState([]);
  const [mesReservations, setMesReservations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDispo, setSelectedDispo] = useState(null);
  const [nbPlaces, setNbPlaces] = useState(1);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    loadDisponibilites();
    loadMesReservations();
  }, []);

  const loadDisponibilites = async () => {
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 2);

      const response = await booking.getDisponibilites({
        date_debut: startDate.toISOString().split('T')[0],
        date_fin: endDate.toISOString().split('T')[0]
      });

      setDisponibilites(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadMesReservations = async () => {
    try {
      const response = await booking.getMesReservations();
      setMesReservations(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getDispoForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return disponibilites.filter(d => d.date === dateStr);
  };

  const handleReserver = async (dispo) => {
    setSelectedDispo(dispo);
    setError('');
    setSuccess('');
  };

  const handleSubmitReservation = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await booking.createReservation({
        disponibilite_id: selectedDispo.id,
        nb_places: parseInt(nbPlaces),
        message
      });

      setSuccess('✅ Pré-réservation envoyée ! Vous recevrez un email de confirmation.');
      setSelectedDispo(null);
      setNbPlaces(1);
      setMessage('');
      
      await loadDisponibilites();
      await loadMesReservations();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la réservation');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReservation = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette réservation ?')) {
      return;
    }

    try {
      await booking.cancelReservation(id);
      setSuccess('Réservation annulée');
      await loadMesReservations();
    } catch (err) {
      setError('Erreur lors de l\'annulation');
    }
  };

  const dispoForSelectedDate = getDispoForDate(selectedDate);

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: 'white' }}>🪁 Réservation Cours Wingfoil</h1>
        <button onClick={() => navigate('/profile')} className="btn btn-secondary">
          👤 Mon Profil
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Calendrier + Disponibilités */}
        <div>
          <div className="card">
            <h2>📅 Calendrier</h2>
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              locale="fr-FR"
              minDate={new Date()}
            />
          </div>

          <div className="card" style={{ marginTop: '20px' }}>
            <h2>Créneaux disponibles le {selectedDate.toLocaleDateString('fr-FR')}</h2>
            {dispoForSelectedDate.length === 0 ? (
              <p style={{ color: '#666' }}>Aucun créneau disponible ce jour</p>
            ) : (
              <div>
                {dispoForSelectedDate.map(dispo => {
                  const placesRestantes = dispo.places_total - dispo.places_reservees;
                  return (
                    <div 
                      key={dispo.id} 
                      style={{ 
                        padding: '15px', 
                        border: '1px solid #ddd', 
                        borderRadius: '8px',
                        marginBottom: '10px',
                        background: placesRestantes > 0 ? '#f8f9fa' : '#ffebee'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong>{dispo.heure_debut} - {dispo.heure_fin}</strong>
                          <p style={{ margin: '5px 0', color: '#666' }}>{dispo.lieu}</p>
                          <p style={{ margin: '5px 0', color: placesRestantes > 0 ? 'green' : 'red' }}>
                            {placesRestantes} place(s) disponible(s) / {dispo.places_total}
                          </p>
                        </div>
                        {placesRestantes > 0 && (
                          <button 
                            onClick={() => handleReserver(dispo)}
                            className="btn btn-primary"
                          >
                            Réserver
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Mes réservations */}
        <div>
          <div className="card">
            <h2>Mes Réservations</h2>

            {error && <div className="error">{error}</div>}
            {success && <div className="success">{success}</div>}

            {mesReservations.length === 0 ? (
              <p style={{ color: '#666' }}>Aucune réservation</p>
            ) : (
              <div>
                {mesReservations.map(res => (
                  <div 
                    key={res.id}
                    style={{ 
                      padding: '15px', 
                      border: '1px solid #ddd', 
                      borderRadius: '8px',
                      marginBottom: '10px',
                      background: res.statut === 'confirmed' ? '#d4edda' : 
                                 res.statut === 'pending' ? '#fff3cd' : '#f8d7da'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <strong>{new Date(res.date).toLocaleDateString('fr-FR')}</strong>
                        <p style={{ margin: '5px 0' }}>{res.heure_debut} - {res.heure_fin}</p>
                        <p style={{ margin: '5px 0' }}>{res.lieu}</p>
                        <p style={{ margin: '5px 0' }}>
                          <strong>Statut:</strong>{' '}
                          {res.statut === 'pending' && '⏳ En attente'}
                          {res.statut === 'confirmed' && '✅ Confirmée'}
                          {res.statut === 'cancelled' && '❌ Annulée'}
                        </p>
                        <p style={{ margin: '5px 0' }}>
                          <strong>Places:</strong> {res.nb_places}
                        </p>
                      </div>
                      {res.statut === 'pending' && (
                        <button 
                          onClick={() => handleCancelReservation(res.id)}
                          className="btn btn-danger"
                          style={{ height: 'fit-content' }}
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Réservation */}
      {selectedDispo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '500px', width: '90%' }}>
            <h2>Confirmer la réservation</h2>

            <p><strong>Date:</strong> {new Date(selectedDispo.date).toLocaleDateString('fr-FR')}</p>
            <p><strong>Horaire:</strong> {selectedDispo.heure_debut} - {selectedDispo.heure_fin}</p>
            <p><strong>Lieu:</strong> {selectedDispo.lieu}</p>

            <form onSubmit={handleSubmitReservation}>
              <div className="form-group">
                <label>Nombre de places *</label>
                <input
                  type="number"
                  min="1"
                  max={selectedDispo.places_total - selectedDispo.places_reservees}
                  value={nbPlaces}
                  onChange={(e) => setNbPlaces(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Message (optionnel)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows="3"
                  placeholder="Niveau, questions..."
                  disabled={loading}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
                  {loading ? 'Envoi...' : 'Confirmer'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setSelectedDispo(null)} 
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;